import { Box, Radio } from '@wix/design-system';
import { CycleUtils, GEOS, wixServerData } from '@wix/package-picker-shared';
import {
  CycleSelectorBadge,
  CycleSelectorItemName,
  RevShareComponents,
  useCycleAvailability,
  useLocalization,
  useMonthlyDisabledFeatures,
  useRevShare,
} from '@wix/package-picker-shared-studio';
import { CycleSelector as PlatformCycleSelector } from '@wix/premium-purchase-platform/@components';
import { PurchaseOffering } from '@wix/premium-purchase-platform/@sdk';
import React from 'react';
import { StudioPackagePickerModule } from '../../modules/studio-package-picker.module';
import { BrazilCyclePrice } from './BrazilCyclePrice';
import { CYCLE_SELECTOR_DATA_HOOKS } from './CycleSelector.datahooks';
import { CycleSelectorDisabledFeatures } from './CycleSelectorDisabledFeatures';
import { useCycleSelectorItemTooltip } from './useCycleSelectorItemTooltip';

interface CycleSelectorItemProps {
  idx: number;
  isSelected: boolean;
  pricingDetail: PurchaseOffering.PricingDetails;
  productId: string;
  selectCycle: (cycle: PurchaseOffering.PurchaseCycle) => void;
}

export const CycleSelectorItem = ({
  idx,
  isSelected,
  pricingDetail,
  productId,
  selectCycle,
}: CycleSelectorItemProps) => {
  const cycle = pricingDetail.cycle;
  const formatPrice = StudioPackagePickerModule.SDK.useFormatPrice();
  const { shouldShowJapaneseExperience } = useLocalization();
  const { experiments } =
    StudioPackagePickerModule.internals.experiments.hooks.useExperiments();
  const isBrazilCyclePrice =
    experiments.isEnabled('specs.premium.BrazilCyclePricing') &&
    wixServerData.get().geo === GEOS.BRAZIL &&
    cycle &&
    !CycleUtils.isMonthly(cycle);
  useMonthlyDisabledFeatures(productId);
  const tooltipData = useCycleSelectorItemTooltip({
    cycle,
    productId,
  });
  const currentProductQuery =
    StudioPackagePickerModule.SDK.switchApiClient.hooks.useGetCurrentProduct(
      {},
    );
  const { getRevShareAmountPerCycle } = useRevShare();

  const { isCycleAvailable } = useCycleAvailability(
    productId,
    cycle || undefined,
  );

  if (!cycle || currentProductQuery.isLoading) {
    return null;
  }

  const isMonthly =
    cycle?.cycleDuration?.unit === PurchaseOffering.DurationUnit.MONTH &&
    cycle?.cycleDuration?.count === 1;

  const showCycleSavingsLabel = getRevShareAmountPerCycle(cycle) > 0;

  return (
    <PlatformCycleSelector.Card.Root
      dataHook={CYCLE_SELECTOR_DATA_HOOKS.item}
      padding="SP4"
      key={idx}
      isSelected={isSelected}
      isOpen={isSelected}
      backgroundColor="white"
      disabled={!isCycleAvailable}
      disabledBackgroundColor="white"
      onSelect={() => selectCycle(cycle)}
      collapseContent={
        <RevShareComponents.CollapsibleCycleWrapper
          cycle={cycle}
          shouldRenderChildren={isMonthly}
        >
          <CycleSelectorDisabledFeatures
            productId={productId}
            selectCycle={selectCycle}
          />
        </RevShareComponents.CollapsibleCycleWrapper>
      }
      borderTop="none"
    >
      <Box direction="horizontal" gap="SP2">
        <Radio checked={isSelected} disabled={!isCycleAvailable} />
        <PlatformCycleSelector.Card.Content
          dataHook="cycle-selector-content"
          width="100%"
          opacity={!isCycleAvailable ? 0.3 : 1}
          display="grid"
          gridTemplateColumns="15% 2% 1fr max-content"
          gap="4px"
        >
          <CycleSelectorItemName
            dataHook={CYCLE_SELECTOR_DATA_HOOKS['item-name']}
            tooltipDataHook={CYCLE_SELECTOR_DATA_HOOKS['item-name-tooltip']}
            cycle={cycle}
            showInMonths={shouldShowJapaneseExperience}
            textProps={{
              weight: 'bold',
            }}
            tooltipContent={tooltipData?.tooltipContent}
            onShowTooltip={tooltipData?.onShowTooltip}
          />

          <PlatformCycleSelector.Card.Separator height="1px" width="8px" />

          {isBrazilCyclePrice ? (
            <BrazilCyclePrice productId={productId} cycle={cycle} />
          ) : (
            <PlatformCycleSelector.Card.Price
              price={
                pricingDetail.catalogPrice?.monthlyTotal?.formattedAmount ??
                undefined
              }
              durationCount={pricingDetail.cycle?.cycleDuration?.count}
              durationUnit={pricingDetail.cycle?.cycleDuration?.unit}
              cycleSavings={
                showCycleSavingsLabel
                  ? formatPrice(
                      Number(pricingDetail.savings?.fixedAmount?.amount),
                      { hideDecimalPlaces: true },
                    )
                  : undefined
              }
            />
          )}

          <Box width="100%" align="right">
            {isCycleAvailable && (
              <CycleSelectorBadge
                dataHook={CYCLE_SELECTOR_DATA_HOOKS.badge}
                tooltipDataHook={CYCLE_SELECTOR_DATA_HOOKS['badge-tooltip']}
                productId={productId}
                cycle={cycle}
                isSelected={isSelected}
                shouldShowTooltip={!!currentProductQuery.data?.currentProduct}
                onShowTooltip={(content) => {
                  StudioPackagePickerModule.internals.bi.onCycleSavingTooltipHover?.(
                    {
                      content,
                    },
                  );
                }}
              />
            )}
          </Box>
        </PlatformCycleSelector.Card.Content>
      </Box>
    </PlatformCycleSelector.Card.Root>
  );
};
